import { useQuery } from '@tanstack/react-query'
import { Alert, Box, Skeleton, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { fetchBalanceSheet } from '../api/endpoints'
import ReportShell, { asAtLabel } from './ReportShell'
import ReportFooter from './ReportFooter'
import ReportToolbar from './ReportToolbar'
import { useDateRange } from './useDateRange'
import { downloadCsv } from './csv'
import { c, fmtMoney } from '../theme'

interface Section {
  code: string; name_en: string; name_ar: string | null; amount: string
  ledgers: { code: string; name_en: string; amount: string }[]
}
interface Sheet {
  as_of: string
  assets: Section[]; liabilities: Section[]; equity: Section[]
  total_assets: string; total_liabilities: string; total_equity: string
  period_profit: string; is_balanced: boolean
}

export default function BalanceSheet({ onLedger }: { onLedger?: (c: string) => void }) {
  const { t, i18n } = useTranslation()
  const { range, setRange, presets } = useDateRange()

  const q = useQuery({
    queryKey: ['balance-sheet', range.to, range.from],
    queryFn: () => fetchBalanceSheet({
      as_of: range.to, fy_start: range.from }) as Promise<Sheet>,
  })
  const bs = q.data

  const block = (label: string, sections: Section[], total: string) => (
    <Box>
      <Typography className="section-label" sx={{ px: 2, pt: 2, display: 'block' }}>
        {label}
      </Typography>
      <Box className="ruled" sx={{ mt: 0.5 }}>
        {sections.map((s) => (
          <Box key={s.code} sx={{ px: 2, py: 1 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>
                {s.name_en}</Typography>
              <span className="num" style={{ fontWeight: 500 }}>
                {fmtMoney(s.amount)}</span>
            </Stack>
            {s.ledgers.map((l) => (
              <Stack key={l.code} direction="row" justifyContent="space-between"
                onClick={() => onLedger?.(l.code)}
                sx={{
                  pl: 2, py: 0.4, cursor: onLedger ? 'pointer' : 'default',
                  '&:hover': { bgcolor: onLedger ? c.primarySoft : undefined },
                }}>
                <Typography sx={{ fontSize: 12.5, color: c.inkMuted }}>
                  {l.code} · {l.name_en}</Typography>
                <span className="num" style={{ fontSize: 12.5, color: c.inkMuted }}>
                  {fmtMoney(l.amount)}</span>
              </Stack>
            ))}
          </Box>
        ))}
      </Box>
      <Stack direction="row" justifyContent="space-between"
        sx={{ px: 2, py: 1, borderTop: `1px solid ${c.border}` }}>
        <span className="section-label">{t('report.total')} {label}</span>
        <span className="num" style={{ fontWeight: 600 }}>{fmtMoney(total)}</span>
      </Stack>
    </Box>
  )

  return (
    <ReportShell titleKey="nav.balanceSheet"
      period={asAtLabel(range.to, i18n.language, t)}
      toolbar={
      <ReportToolbar range={range} setRange={setRange} presets={presets} singleDate
        pdf={{ path: 'balance-sheet.pdf',
               params: { as_of: range.to, fy_start: range.from } }}
        onExport={bs ? () => downloadCsv(`balance-sheet-${bs.as_of}`, [
          ['Section', 'Group', 'Code', 'Account', 'Amount'],
          ...(['assets', 'liabilities', 'equity'] as const).flatMap((k) =>
            bs[k].flatMap((s) => s.ledgers.map((l) =>
              [k, s.name_en, l.code, l.name_en, l.amount]))),
          ['', '', '', 'Profit for the period', bs.period_profit],
        ]) : undefined} />
      }>

      {q.isLoading ? (
        <Box sx={{ p: 2 }}><Skeleton /><Skeleton /><Skeleton /></Box>
      ) : bs ? (
        <>
          <Box sx={{
            display: 'grid', gap: 0,
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          }}>
            <Box sx={{ borderInlineEnd: { md: `1px solid ${c.divider}` } }}>
              {block(t('report.assets'), bs.assets, bs.total_assets)}
            </Box>
            <Box>
              {block(t('report.liabilities'), bs.liabilities, bs.total_liabilities)}
              {block(t('report.equity'), bs.equity, bs.total_equity)}
              <Stack direction="row" justifyContent="space-between"
                sx={{ px: 2, py: 1 }}>
                <Typography sx={{ fontSize: 13.5 }}>
                  {t('report.periodProfit')}</Typography>
                <span className={`num ${Number(bs.period_profit) < 0
                  ? 'num-cr' : 'num-dr'}`} style={{ fontWeight: 500 }}>
                  {fmtMoney(bs.period_profit)}</span>
              </Stack>
            </Box>
          </Box>

          <Stack direction="row" justifyContent="space-between"
            sx={{ px: 2, py: 1.5, borderTop: `1px solid ${c.ink}` }}>
            <span className="section-label">
              {bs.is_balanced ? t('report.balanced') : t('report.outOfBalance')}
            </span>
            <span className="num" style={{ fontWeight: 600 }}>
              {fmtMoney(bs.total_assets)}</span>
          </Stack>

          {!bs.is_balanced && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">{t('report.outOfBalance')}</Alert>
            </Box>
          )}
        </>
      ) : null}
      <ReportFooter />
    </ReportShell>
  )
}
